import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import loadable from '@loadable/component';
import ms from 'ms';
import { useEffect, useState } from 'react';

import { ItemCardAuctionName } from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryBidCount,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';
import ItemCardImage from '../../components/ItemCardImage/ItemCardImage';
import { ItemCardPriceRow } from '../../components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '../../components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '../../components/ItemCardTitle/ItemCardTitle';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

const FollowedSearchItemPageItemCard: ItemCardComponent = ({ className, itemId }) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const [followedSearchItemCardHovered, setFollowedSearchItemCardHovered] = useState(false);

    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));

    // Logic for when an auction is live
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;

    const previousCatalogStatus = usePrevious(catalogStatus);

    useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [catalogId, catalogStatus, sellerId, dispatch, previousCatalogStatus]);

    const isMobile = useIsMobileLayout();

    const catalogLiveWithinAWeek = catalogStatus !== 'done' && Date.now() > saleStartTs * 1000 - ms('7d');
    const shouldNotShowAuctionName =
        (catalogStatus === 'live' || catalogStatus === 'online') && isMobile && catalogLiveWithinAWeek;
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;
    const showDateWithOnlyYear = catalogStatus === 'done';
    const isBuyNowOnMobile = isMobile && Boolean(buyNowStatus);
    const showAuctionName = !shouldNotShowAuctionName || !isBuyNowOnMobile;
    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;

    return (
        <div
            className={cn('relative min-w-0', className)}
            data-testid="my-bids-item-card"
        >
            {comingUp && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="itemCard"
                />
            )}
            <section
                className="relative mb-xs aspect-square"
                data-testid="image-container"
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            >
                <span className="absolute right-xs top-xs z-[2]">
                    <ItemCardSaveIcon
                        canShowEasyShipping
                        itemId={itemId}
                    />
                </span>
                <ItemCardImage
                    itemId={itemId}
                    showBidStatus
                    useInFluidGrid
                />
            </section>

            <span className="mb-2xs flex justify-between whitespace-nowrap">
                <div className="mr-xs flex-grow">
                    <ItemCardShortDate
                        allowBidding
                        itemId={itemId}
                        showDateWithoutYear={showDateWithoutYear}
                        showOnlyYear={showDateWithOnlyYear}
                    />
                </div>
                {showAuctionName && <ItemCardAuctionName itemId={itemId} />}
            </span>

            <ItemCardTitle
                isHovered={followedSearchItemCardHovered}
                itemId={itemId}
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            />

            <div
                className="flex justify-between"
                onMouseEnter={() => {
                    setFollowedSearchItemCardHovered(true);
                }}
                onMouseLeave={() => {
                    setFollowedSearchItemCardHovered(false);
                }}
            >
                <div className="flex items-center">
                    <ItemCardPriceRow
                        isHovered={followedSearchItemCardHovered}
                        itemId={itemId}
                    />
                    {!isSold && !isPassed && !isPending && bidCount > 0 && (
                        <Caption
                            className={cn(
                                'ml-xs text-text-secondary',
                                followedSearchItemCardHovered && 'cursor-pointer text-link-hover underline'
                            )}
                            data-testid="bid-count"
                            onMouseOut={() => {
                                setFollowedSearchItemCardHovered(false);
                            }}
                            onMouseOver={() => {
                                setFollowedSearchItemCardHovered(true);
                            }}
                        >
                            {formatMessage(['itemBidding', 'bid'], { values: { bidCount } })}
                        </Caption>
                    )}
                </div>
                {isLoggedIn && (
                    <ItemCardMenu
                        itemId={itemId}
                        showIcon={followedSearchItemCardHovered}
                    />
                )}
            </div>
        </div>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: FollowedSearchItemPageItemCard,
};

export default info;
